/** NPC source window adapted from the existing PC twin; all rule kernels stay shared. */
import type { ItemFieldRow } from './item-field-reader';
import type { FromBaseRow } from './from-base-context';
type FromBasePlayerInput = FromBaseRow;
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing } from './from-base-context';

import { createFromBaseNpcGrantContext, appendNpcRootAbility } from './from-base-npc-stages';
import type { FromBaseNpcMetadata } from './from-base-npc-stages';
import { createSkillRequirementChecker } from './skill-requirements';
import { createAbilityFinalizerGates } from './ability-finalizer-gates';
import { readItemScalarLayerSlot, readOverrideEntryAbilityId } from './layer-cell-readers';
import { readLayerListEntryUuid, readItemUuidSlot } from './inventory-raw-readers';
import { appendCompiledLayerInPlace, appendPhpCompileArray } from './status-row-construction';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
/** The full-skills tail, after stored entries and before signatures. Definitions
 * are finalized in the later reference-expansion window. */
export function constructNpcFeaturesFromBase(base: FromBasePlayerInput, metadata: import('./from-base-player-resources').FromBasePlayerResourceMetadata, input: FromBaseRow): FromBaseRow {
    if (!metadata.features) throw new FromBaseInputMissing('natural-skill-feature-metadata', phpJsonString(base.entity_id ?? ''));
    const host = fromBaseClone(input), { readers, ownReaders, definition, deps } = createFromBaseNpcGrantContext(base, metadata, host, 'integer-rollup-full-skills');
    const features = metadata.features;
    const abilityId = (entry: unknown) => readAbilityEntryId(entry, { itemField: readers.itemField, resolveCarrierRef: definition });
    const entries = (row: ItemFieldRow | null, key: string): unknown[] => Object.values(readers.layerEntries(row, key));
    const field = (row: ItemFieldRow | null, key: string) => readers.layerValue(row, key);
    const computed = host.computed as FromBaseRow, creature = computed.creature as FromBaseRow;
    const skills = fromBaseRecord(creature.skills) ? creature.skills : {};
    const tier = (variable: string): number | null => {
        const skill = metadata.skills.find(row => row.variable_name === variable);
        if (!skill || !Object.keys(skills).length) return null;
        const stored = field(skills, variable);
        return fromBaseRecord(stored) ? phpJsonInt(field(stored, 'tier_level'))
            : ['defensive_skill', 'innate_skill'].includes(skill.item_type) ? 1 : 0;
    };
    const resolved = (entry: unknown): ItemFieldRow | null => {
        if (fromBaseRecord(entry) && fromBaseRecord(entry.item_layer_types)) return entry;
        const id = readItemScalarLayerSlot(entry);
        return fromBaseUuid(id) ? definition(id) : null;
    };
    // These unions follow the native creature readers, including their different
    // identity extractors and first-visible-entry precedence.
    const union = (key: string, derived: unknown, id: (entry: unknown) => string, discardEmpty = false): unknown[] => {
        const list = (metadata as FromBaseNpcMetadata).postReference ? entries(host, key) : Object.values(ownReaders.layerEntries(host, key));
        const seen = new Set(list.map(id).filter(Boolean));
        for (const entry of derived !== null && typeof derived === 'object' ? Object.values(derived) : []) {
            const value = id(entry);
            if ((discardEmpty && !value) || (value && seen.has(value))) continue;
            if (value) seen.add(value);
            list.push(entry);
        }
        return list;
    };
    const weapons = () => union('natural_weapon', entries(creature, 'natural_weapon'), readItemScalarLayerSlot);
    const abilities = (key: string) => union(key, entries(fromBaseRecord(creature.skill_ability) ? creature.skill_ability : {}, key), readOverrideEntryAbilityId);
    const rootEntries = (key: string): unknown[] => entries({ [key]: host[key] }, key);
    const appendRoot = (key: string, value: unknown) => {
        // The post-mirror skill pass appends a fresh root grant; its finalized
        // ability bucket must stay readable while derived skill shells rebuild.
        const slot = host[key];
        if (slot != null && typeof slot !== 'object') throw new TypeError('Cannot append a natural-skill grant to a scalar');
        if ((metadata as FromBaseNpcMetadata).postReference) host[key] = appendPhpCompileArray((slot ?? []) as ItemFieldRow, value);
        else appendNpcRootAbility(host, key, value);
    };
    const naturalSkillId = metadata.skills.find(row => row.variable_name === 'natural_weapon_skill')?.item_id;
    if (!fromBaseUuid(naturalSkillId)) throw new FromBaseInputMissing('natural-weapon-skill-registry', phpJsonString(base.entity_id ?? readers.itemField(input, 'item_id')));
    const skillGate = createSkillRequirementChecker({ ...deps, skillEntries: metadata.skills });
    const requirements = (ability: ItemFieldRow) => features.tiers.map(row => ({ ...row, entries: entries(ability, row.requires_skill_tier) }))
        .filter(row => row.entries.length);
    const classificationGrants = (): void => {
        if (readers.itemField(host, 'item_type') !== 'player_character' || tier('natural_weapon_skill') === null) return;
        if (!weapons().some(entry => {
            const slot = readItemUuidSlot(field(resolved(entry), 'equipment_slot'));
            return slot !== '' && slot !== metadata.equipmentSlots.head;
        })) return;
        const parent = definition(features.naturalWeaponClassificationId);
        if (!parent) return;
        for (const key of metadata.abilityTypes) for (const entry of entries(parent, key)) {
            const id = readLayerListEntryUuid(entry);
            if (!fromBaseUuid(id)) continue;
            const ability = definition(id);
            if (!ability || requirements(ability).some(row => row.entries.some(value => readItemScalarLayerSlot(value) === naturalSkillId)
                && tier('natural_weapon_skill')! < row.skill_tier_level)) continue;
            if (rootEntries(key).some(value => abilityId(value) === id)) continue;
            appendRoot(key, { ability_id: id, item_name: readers.itemField(ability, 'item_name') ?? deps.resolveItemName(id),
                source: features.naturalWeaponClassificationId, requires_level: 1, item_arr: ability,
                built_from: features.naturalWeaponClassificationId, classification_granted: true });
        }
    };
    const armor = (): void => {
        const level = tier('natural_armor_skill'), grant = features.armorReactions.find(row => row.variable_name === `natural_armor_skill_tier_${level}`);
        if (!grant) return;
        const ability = definition(grant.item_id);
        if (rootEntries('reaction_ability').some(entry => abilityId(entry) === grant.item_id)) return;
        appendRoot('reaction_ability', { ability_id: grant.item_id, item_name: readers.itemField(ability, 'item_name'), source: 'natural_armor_skill',
            requires_level: 1, item_arr: ability, built_from: 'natural_armor_skill' });
    };
    const average = (row: ItemFieldRow | null): number | null => {
        const dice = field(row, 'dice'), die = field(row, 'die'), mod = field(row, 'mod');
        if (!isPhpNumeric(dice) || !isPhpNumeric(die) || phpJsonFloat(dice) <= 0 || phpJsonFloat(die) <= 0) return null;
        return phpJsonFloat(dice) * (phpJsonFloat(die) + 1) / 2 + (isPhpNumeric(mod) ? phpJsonFloat(mod) : 0);
    };
    const slam = (): void => {
        if (readers.itemField(host, 'item_type') === 'lair') return;
        const level = tier('natural_weapon_skill');
        if (level === null) return;
        const grant = features.weaponGrants.find(row => level >= phpJsonInt(row.variable_name.replace('min_skill_tier_', '')));
        if (!grant) return;
        const grantIds = new Set(features.weaponGrants.map(row => row.item_id.toLowerCase()));
        const previous = entries(creature, 'natural_weapon');
        if (creature.natural_weapon !== null && typeof creature.natural_weapon === 'object') {
            const kept = previous.filter(entry => !grantIds.has(readItemScalarLayerSlot(entry).toLowerCase()));
            if (kept.length) creature.natural_weapon = kept; else delete creature.natural_weapon;
        }
        let main = phpJsonInt(field(host, 'count_main_hand_equipped')) > 0, off = phpJsonInt(field(host, 'count_off_hand_equipped')) > 0;
        const existing = weapons().filter(entry => fromBaseUuid(readItemScalarLayerSlot(entry))).map(resolved);
        for (const row of existing) {
            const slot = readItemUuidSlot(field(row, 'equipment_slot'));
            if (slot === metadata.equipmentSlots.main_hand || slot === metadata.equipmentSlots.both_hands) main = true;
            if (slot === metadata.equipmentSlots.off_hand || slot === metadata.equipmentSlots.both_hands) off = true;
        }
        if (main && off) return;
        const weapon = definition(grant.item_id);
        if (!weapon) return;
        const damage = average(weapon);
        if (existing.length && (damage === null || existing.some(row => { const value = average(row); return value === null || value >= damage; }))) return;
        if (!weapons().some(entry => readItemScalarLayerSlot(entry) === grant.item_id)) {
            creature.natural_weapon = [...entries(creature, 'natural_weapon'),
                { items: { item_id: grant.item_id, item_name: readers.itemField(weapon, 'item_name') ?? '', item_type: readers.itemField(weapon, 'item_type') ?? 'natural_weapon' } }];
        }
        classificationGrants();
    };
    const encounter = fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : {};
    if (field(encounter, 'encounter_creature_id') !== null) {
        armor(); slam();
        // PHP's early fist check uses strict zero, unlike the integer-coerced
        // occupancy check in slam(). Preserve that distinction on captured rows.
        if (((field(host, 'count_main_hand_equipped') ?? 0) === 0 || (field(host, 'count_off_hand_equipped') ?? 0) === 0)
            && tier('natural_weapon_skill') === 0 && !weapons().length) appendCompiledLayerInPlace(host, 'natural_weapon', features.fistId);
        for (const parent of features.parentSkills) {
            const skill = definition(parent.item_id);
            if (!skill) continue;
            for (const key of metadata.abilityTypes) for (const entry of entries(skill, key)) {
                const id = readLayerListEntryUuid(entry);
                if (!fromBaseUuid(id)) continue;
                const ability = definition(id);
                if (!ability) continue;
                const gates = requirements(ability);
                if (gates.length && !gates.some(gate => gate.entries.some(value => {
                    const variable = metadata.skills.find(row => row.item_id === readItemScalarLayerSlot(value))?.variable_name;
                    const stored = variable ? field(skills, variable) : null;
                    return fromBaseRecord(stored) && phpJsonInt(field(stored, 'tier_level')) >= gate.skill_tier_level;
                }))) continue;
                if (entries(host, key).some(value => abilityId(value) === id)) continue;
                appendCompiledLayerInPlace(host, key, { ability_id: id, item_name: readers.itemField(ability, 'item_name') ?? 'Unknown', source: parent.item_id,
                    requires_level: 1, item_arr: ability, built_from: parent.item_id, nested_granted: true });
            }
        }
    }
    const surface = (parent: ItemFieldRow, keys: readonly string[], origin: string): void => {
        for (const key of keys) for (const entry of entries(parent, key)) {
            const id = readItemScalarLayerSlot(entry);
            if (!fromBaseUuid(id) || abilities(key).some(value => readOverrideEntryAbilityId(value) === id)) continue;
            const ability = definition(id);
            if (!ability || !skillGate(ability, host)) continue;
            const shell: FromBaseRow = { items: { item_id: id, item_name: readers.itemField(ability, 'item_name') ?? '',
                item_type: readers.itemField(ability, 'item_type') ?? key }, built_from: origin };
            const overrides = entries(ability, 'override_ability').map(readItemScalarLayerSlot).filter(Boolean);
            if (overrides.length) shell.override_ability = overrides;
            const layers = fromBaseRecord(ability) && fromBaseRecord(ability.item_layer_types) ? { ...ability.item_layer_types } : {};
            delete layers.override_ability;
            for (const gate of features.tiers) {
                const values = entries(ability, gate.requires_skill_tier);
                if (values.length) layers[gate.requires_skill_tier] = values;
            }
            if (Object.keys(layers).length) shell.item_layer_types = layers;
            const derived = (creature.skill_ability ??= {}) as FromBaseRow;
            derived[key] = [...Object.values(derived[key] ?? {}), shell];
        }
    };
    if (['creature', 'player_character'].includes(phpJsonString(readers.itemField(host, 'item_type')))) {
        slam();
        for (const entry of weapons()) { const weapon = resolved(entry); if (weapon) surface(weapon, ['attack_ability', 'opportunity_attack_ability'], 'natural_weapon'); }
        const sources = new Map<string, unknown>();
        for (const descriptor of features.tiers) for (const entry of union(descriptor.variable_name,
            entries(fromBaseRecord(creature.skill_tier_grants) ? creature.skill_tier_grants : {}, descriptor.variable_name), readLayerListEntryUuid, true)) {
            const id = readItemScalarLayerSlot(entry);
            if (fromBaseUuid(id) && !sources.has(id)) sources.set(id, entry);
        }
        if (fromBaseRecord(host.player_characters)) for (const [variable, entry] of Object.entries(skills)) {
            if (!fromBaseRecord(entry) || !phpJsonTruthy(field(entry, 'raw_sp'))) continue;
            const id = field(entry, 'item_id') ?? metadata.skills.find(row => row.variable_name === variable)?.item_id;
            if (fromBaseUuid(id) && !sources.has(id)) sources.set(id, id);
        }
        for (const entry of sources.values()) { const skill = resolved(entry); if (skill) surface(skill, metadata.abilityTypes, 'skill_tier'); }
    }
    const overridden = createAbilityFinalizerGates({ ...deps, defenseIntegerLayers: metadata.defenseIntegerLayers }).override(host) as FromBaseRow;
    const isFist = (entry: unknown) => readLayerListEntryUuid(entry).toLowerCase() === features.fistId.toLowerCase();
    const visible = weapons();
    if (visible.some(isFist) && (readers.itemField(host, 'item_type') === 'lair' || visible.some(entry => !isFist(entry)) || (tier('natural_weapon_skill') ?? 0) > 0)) {
        for (const bucket of [overridden, overridden.item_layer_types].filter(fromBaseRecord)) {
            const slot = bucket.natural_weapon;
            if (slot != null && typeof slot === 'object' && Object.prototype.hasOwnProperty.call(slot, '0')) {
                const kept = Object.values(slot).filter(entry => !isFist(entry));
                if (kept.length) bucket.natural_weapon = kept; else delete bucket.natural_weapon;
            } else if (isFist(slot)) delete bucket.natural_weapon;
        }
    }
    if (overridden.natural_weapon !== null && typeof overridden.natural_weapon === 'object') {
        const seen = new Set<string>();
        overridden.natural_weapon = entries(overridden, 'natural_weapon').map(entry => readItemScalarLayerSlot(entry).split('|', 1)[0]!)
            .filter(id => { if (!fromBaseUuid(id) || seen.has(id)) return false; seen.add(id); return true; });
    }
    return overridden;
}
